import { useState, useEffect, createContext, useContext } from "react";
import { hasSupabase } from "./lib/supabase.js";
import { signIn as authSignIn, signUp as authSignUp, signOut as authSignOut, getCurrentUser, onAuthChange } from "./lib/auth.js";
import { fetchMyBriefs, fetchOpenBriefs, insertBrief, fetchProposalsForBrief, insertProposal, fetchAllCaterers, approveCaterer, rejectCaterer, suspendCaterer, fetchAdminMetrics, signUpForInterest } from "./lib/db.js";
import { checkVat } from "./lib/vies.js";
import { startCheckout, openPortal } from "./lib/billing.js";

// ============================================================
// DESIGN SYSTEM
// Refined institutional — clean, authoritative, trustworthy
// Palette: deep navy + warm sand + sharp copper accent
// ============================================================

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --navy: #0f1e35;
    --navy-mid: #1a3050;
    --navy-light: #243d5c;
    --copper: #c4773a;
    --copper-light: #d98f52;
    --sand: #f5f0e8;
    --sand-dark: #ede5d6;
    --white: #ffffff;
    --text: #1a2332;
    --text-mid: #4a5568;
    --text-light: #718096;
    --border: #e2d9cc;
    --success: #2d7a4f;
    --error: #c0392b;
    --warning: #d97706;
    --radius: 8px;
    --shadow: 0 2px 12px rgba(15,30,53,0.08);
    --shadow-lg: 0 8px 32px rgba(15,30,53,0.14);
  }

  body {
    font-family: 'DM Sans', sans-serif;
    background: var(--sand);
    color: var(--text);
    line-height: 1.6;
    min-height: 100vh;
  }

  h1, h2, h3 { font-family: 'Playfair Display', serif; }

  /* LAYOUT */
  .app { display: flex; flex-direction: column; min-height: 100vh; }

  /* NAV */
  .nav {
    background: var(--navy);
    padding: 0 2rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 64px;
    position: sticky;
    top: 0;
    z-index: 100;
    box-shadow: 0 2px 16px rgba(0,0,0,0.2);
  }
  .nav-logo {
    font-family: 'Playfair Display', serif;
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--white);
    letter-spacing: -0.5px;
    cursor: pointer;
  }
  .nav-logo span { color: var(--copper); }
  .nav-links { display: flex; gap: 0.5rem; align-items: center; }
  .nav-btn {
    background: none;
    border: none;
    color: rgba(255,255,255,0.75);
    font-family: 'DM Sans', sans-serif;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    padding: 0.5rem 1rem;
    border-radius: var(--radius);
    transition: all 0.2s;
  }
  .nav-btn:hover { color: var(--white); background: rgba(255,255,255,0.1); }
  .nav-btn.active { color: var(--copper-light); }
  .nav-pill {
    background: var(--copper);
    color: var(--white);
    font-family: 'DM Sans', sans-serif;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    padding: 0.5rem 1.25rem;
    border-radius: 100px;
    border: none;
    transition: all 0.2s;
  }
  .nav-pill:hover { background: var(--copper-light); transform: translateY(-1px); }
  .nav-user {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: var(--white);
    font-size: 0.875rem;
  }
  .nav-avatar {
    width: 36px; height: 36px;
    background: var(--copper);
    border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-weight: 700;
    font-size: 0.875rem;
    cursor: pointer;
  }
  .lang-switcher { display: flex; gap: 4px; }
  .lang-btn {
    background: none; border: 1px solid rgba(255,255,255,0.2);
    color: rgba(255,255,255,0.6); font-size: 0.7rem; font-weight: 600;
    padding: 2px 6px; border-radius: 4px; cursor: pointer;
    font-family: 'DM Sans', sans-serif; text-transform: uppercase;
    transition: all 0.15s;
  }
  .lang-btn:hover, .lang-btn.active { color: var(--white); border-color: var(--copper); background: rgba(196,119,58,0.2); }

  /* MAIN CONTENT */
  .main { flex: 1; }

  /* HERO */
  .hero {
    background: var(--navy);
    padding: 5rem 2rem 4rem;
    text-align: center;
    position: relative;
    overflow: hidden;
  }
  .hero::before {
    content: '';
    position: absolute; inset: 0;
    background: radial-gradient(ellipse at 30% 50%, rgba(196,119,58,0.15) 0%, transparent 60%),
                radial-gradient(ellipse at 70% 30%, rgba(36,61,92,0.5) 0%, transparent 50%);
  }
  .hero-content { position: relative; max-width: 760px; margin: 0 auto; }
  .hero-badge {
    display: inline-block;
    background: rgba(196,119,58,0.2);
    border: 1px solid rgba(196,119,58,0.4);
    color: var(--copper-light);
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    padding: 0.4rem 1rem;
    border-radius: 100px;
    margin-bottom: 1.5rem;
  }
  .hero h1 {
    font-size: clamp(2.2rem, 5vw, 3.5rem);
    color: var(--white);
    line-height: 1.15;
    margin-bottom: 1.25rem;
    font-weight: 700;
  }
  .hero h1 em { color: var(--copper-light); font-style: normal; }
  .hero p {
    font-size: 1.125rem;
    color: rgba(255,255,255,0.7);
    max-width: 540px;
    margin: 0 auto 2.5rem;
    line-height: 1.7;
  }
  .hero-ctas { display: flex; gap: 1rem; justify-content: center; flex-wrap: wrap; }

  /* STATS BAR */
  .stats-bar {
    background: var(--white);
    border-bottom: 1px solid var(--border);
    padding: 1.5rem 2rem;
    display: flex;
    justify-content: center;
    gap: 3rem;
    flex-wrap: wrap;
  }
  .stat { text-align: center; }
  .stat-number {
    font-family: 'Playfair Display', serif;
    font-size: 1.75rem;
    font-weight: 700;
    color: var(--navy);
    display: block;
  }
  .stat-label { font-size: 0.8rem; color: var(--text-light); font-weight: 500; letter-spacing: 0.5px; }

  /* SECTIONS */
  .section { padding: 4rem 2rem; }
  .section-inner { max-width: 1100px; margin: 0 auto; }
  .section-header { text-align: center; margin-bottom: 3rem; }
  .section-header h2 { font-size: 2rem; color: var(--navy); margin-bottom: 0.75rem; }
  .section-header p { color: var(--text-mid); font-size: 1rem; max-width: 500px; margin: 0 auto; }

  /* CARDS */
  .card {
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 1.5rem;
    box-shadow: var(--shadow);
    transition: box-shadow 0.2s, transform 0.2s;
  }
  .card:hover { box-shadow: var(--shadow-lg); transform: translateY(-2px); }
  .card-grid { display: grid; gap: 1.25rem; }
  .card-grid.cols-2 { grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); }
  .card-grid.cols-3 { grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); }

  /* BRIEF CARD */
  .brief-card { cursor: pointer; }
  .brief-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 0.75rem; }
  .brief-title { font-family: 'Playfair Display', serif; font-size: 1.1rem; font-weight: 600; color: var(--navy); }
  .brief-org { font-size: 0.8rem; color: var(--text-light); margin-bottom: 0.75rem; }
  .brief-meta { display: flex; gap: 1rem; flex-wrap: wrap; margin-bottom: 1rem; }
  .brief-meta-item { font-size: 0.8rem; color: var(--text-mid); display: flex; align-items: center; gap: 0.3rem; }
  .brief-footer { display: flex; justify-content: space-between; align-items: center; padding-top: 0.75rem; border-top: 1px solid var(--border); }
  .brief-budget { font-weight: 600; color: var(--navy); font-size: 0.9rem; }
  .brief-deadline { font-size: 0.75rem; color: var(--text-light); }

  /* BADGES */
  .badge {
    display: inline-flex; align-items: center;
    font-size: 0.7rem; font-weight: 600; letter-spacing: 0.5px;
    text-transform: uppercase; padding: 0.25rem 0.6rem;
    border-radius: 100px;
  }
  .badge-open { background: #dcfce7; color: #166534; }
  .badge-pending { background: #fef3c7; color: #92400e; }
  .badge-closed { background: #f1f5f9; color: #64748b; }
  .badge-awarded { background: #dbeafe; color: #1e40af; }
  .badge-approved { background: #dcfce7; color: #166534; }
  .badge-rejected { background: #fee2e2; color: #991b1b; }
  .badge-draft { background: #f1f5f9; color: #64748b; }

  /* BUTTONS */
  .btn {
    display: inline-flex; align-items: center; justify-content: center; gap: 0.5rem;
    font-family: 'DM Sans', sans-serif; font-weight: 600; font-size: 0.9rem;
    padding: 0.65rem 1.5rem; border-radius: var(--radius);
    border: none; cursor: pointer; transition: all 0.2s;
    text-decoration: none;
  }
  .btn-primary { background: var(--copper); color: var(--white); }
  .btn-primary:hover { background: var(--copper-light); transform: translateY(-1px); box-shadow: 0 4px 12px rgba(196,119,58,0.3); }
  .btn-secondary { background: var(--navy); color: var(--white); }
  .btn-secondary:hover { background: var(--navy-mid); transform: translateY(-1px); }
  .btn-outline { background: transparent; color: var(--navy); border: 1.5px solid var(--navy); }
  .btn-outline:hover { background: var(--navy); color: var(--white); }
  .btn-ghost { background: transparent; color: var(--text-mid); border: 1.5px solid var(--border); }
  .btn-ghost:hover { background: var(--sand-dark); border-color: var(--text-light); }
  .btn-danger { background: var(--error); color: var(--white); }
  .btn-sm { padding: 0.4rem 1rem; font-size: 0.8rem; }
  .btn-lg { padding: 0.85rem 2rem; font-size: 1rem; border-radius: 10px; }
  .btn:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }

  /* FORMS */
  .form-group { margin-bottom: 1.25rem; }
  .form-label { display: block; font-size: 0.85rem; font-weight: 600; color: var(--text); margin-bottom: 0.4rem; }
  .form-label.required::after { content: ' *'; color: var(--error); }
  .form-input, .form-select, .form-textarea {
    width: 100%; padding: 0.65rem 0.9rem;
    border: 1.5px solid var(--border); border-radius: var(--radius);
    font-family: 'DM Sans', sans-serif; font-size: 0.9rem; color: var(--text);
    background: var(--white); transition: border-color 0.2s, box-shadow 0.2s;
    outline: none;
  }
  .form-input:focus, .form-select:focus, .form-textarea:focus {
    border-color: var(--copper); box-shadow: 0 0 0 3px rgba(196,119,58,0.1);
  }
  .form-textarea { min-height: 100px; resize: vertical; }
  .form-hint { font-size: 0.75rem; color: var(--text-light); margin-top: 0.3rem; }
  .form-error { font-size: 0.75rem; color: var(--error); margin-top: 0.3rem; }
  .form-row { display: grid; gap: 1rem; grid-template-columns: 1fr 1fr; }
  .form-row.cols-3 { grid-template-columns: 1fr 1fr 1fr; }

  /* CHECKBOXES & PILLS */
  .checkbox-group { display: flex; flex-wrap: wrap; gap: 0.5rem; }
  .checkbox-pill input { display: none; }
  .checkbox-pill label {
    display: inline-flex; align-items: center; gap: 0.3rem;
    padding: 0.3rem 0.75rem; border-radius: 100px;
    border: 1.5px solid var(--border);
    font-size: 0.8rem; font-weight: 500; cursor: pointer;
    transition: all 0.15s; color: var(--text-mid);
  }
  .checkbox-pill input:checked + label {
    background: var(--navy); color: var(--white); border-color: var(--navy);
  }

  /* DASHBOARD */
  .dashboard { display: flex; min-height: calc(100vh - 64px); }
  .sidebar {
    width: 240px; background: var(--white);
    border-right: 1px solid var(--border);
    padding: 1.5rem 0; flex-shrink: 0;
  }
  .sidebar-section { margin-bottom: 1.5rem; }
  .sidebar-title {
    font-size: 0.65rem; font-weight: 700; letter-spacing: 1.5px;
    text-transform: uppercase; color: var(--text-light);
    padding: 0 1.25rem; margin-bottom: 0.5rem;
  }
  .sidebar-item {
    display: flex; align-items: center; gap: 0.75rem;
    padding: 0.65rem 1.25rem;
    font-size: 0.875rem; font-weight: 500; color: var(--text-mid);
    cursor: pointer; border-left: 3px solid transparent;
    transition: all 0.15s;
  }
  .sidebar-item:hover { background: var(--sand); color: var(--text); }
  .sidebar-item.active { color: var(--navy); background: var(--sand); border-left-color: var(--copper); font-weight: 600; }
  .sidebar-icon { width: 18px; text-align: center; }
  .sidebar-badge {
    margin-left: auto; background: var(--copper); color: var(--white);
    font-size: 0.65rem; font-weight: 700; padding: 2px 6px; border-radius: 100px;
  }

  .dash-content { flex: 1; padding: 2rem; overflow-y: auto; }
  .dash-header { margin-bottom: 2rem; }
  .dash-header h2 { font-size: 1.6rem; color: var(--navy); }
  .dash-header p { color: var(--text-mid); font-size: 0.9rem; margin-top: 0.25rem; }

  /* METRIC CARDS */
  .metrics { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 1rem; margin-bottom: 2rem; }
  .metric-card {
    background: var(--white); border: 1px solid var(--border);
    border-radius: var(--radius); padding: 1.25rem;
    box-shadow: var(--shadow);
  }
  .metric-value { font-family: 'Playfair Display', serif; font-size: 2rem; font-weight: 700; color: var(--navy); }
  .metric-label { font-size: 0.8rem; color: var(--text-light); margin-top: 0.2rem; }
  .metric-change { font-size: 0.75rem; margin-top: 0.5rem; }
  .metric-change.up { color: var(--success); }
  .metric-change.down { color: var(--error); }

  /* TABLE */
  .table-wrap { background: var(--white); border: 1px solid var(--border); border-radius: var(--radius); overflow: hidden; box-shadow: var(--shadow); }
  table { width: 100%; border-collapse: collapse; }
  th { background: var(--sand); padding: 0.75rem 1rem; text-align: left; font-size: 0.75rem; font-weight: 700; letter-spacing: 0.5px; text-transform: uppercase; color: var(--text-mid); border-bottom: 1px solid var(--border); }
  td { padding: 0.9rem 1rem; font-size: 0.875rem; color: var(--text); border-bottom: 1px solid var(--sand); }
  tr:last-child td { border-bottom: none; }
  tr:hover td { background: var(--sand); }

  /* MODAL */
  .modal-overlay {
    position: fixed; inset: 0; background: rgba(15,30,53,0.6);
    display: flex; align-items: center; justify-content: center;
    z-index: 1000; padding: 1rem; backdrop-filter: blur(4px);
    animation: fadeIn 0.2s ease;
  }
  .modal {
    background: var(--white); border-radius: 12px;
    box-shadow: 0 24px 64px rgba(0,0,0,0.2);
    width: 100%; max-width: 640px; max-height: 90vh;
    overflow-y: auto; animation: slideUp 0.25s ease;
  }
  .modal-header {
    padding: 1.5rem 1.5rem 1rem;
    border-bottom: 1px solid var(--border);
    display: flex; justify-content: space-between; align-items: flex-start;
  }
  .modal-header h3 { font-size: 1.25rem; color: var(--navy); }
  .modal-body { padding: 1.5rem; }
  .modal-footer {
    padding: 1rem 1.5rem;
    border-top: 1px solid var(--border);
    display: flex; justify-content: flex-end; gap: 0.75rem;
  }
  .modal-close {
    background: none; border: none; cursor: pointer;
    color: var(--text-light); font-size: 1.25rem; padding: 0.25rem;
    line-height: 1; transition: color 0.15s;
  }
  .modal-close:hover { color: var(--text); }

  /* ALERT */
  .alert { padding: 0.875rem 1rem; border-radius: var(--radius); font-size: 0.875rem; margin-bottom: 1rem; }
  .alert-success { background: #f0fdf4; border: 1px solid #bbf7d0; color: #166534; }
  .alert-error { background: #fef2f2; border: 1px solid #fecaca; color: #991b1b; }
  .alert-info { background: #eff6ff; border: 1px solid #bfdbfe; color: #1e40af; }

  /* PRICING */
  .pricing-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 1.5rem; max-width: 960px; margin: 0 auto; }
  .pricing-card {
    background: var(--white); border: 2px solid var(--border);
    border-radius: 12px; padding: 2rem; position: relative;
    transition: all 0.2s;
  }
  .pricing-card:hover { transform: translateY(-4px); box-shadow: var(--shadow-lg); }
  .pricing-card.featured { border-color: var(--copper); }
  .pricing-badge {
    position: absolute; top: -12px; left: 50%; transform: translateX(-50%);
    background: var(--copper); color: var(--white);
    font-size: 0.7rem; font-weight: 700; letter-spacing: 1px;
    text-transform: uppercase; padding: 0.25rem 1rem; border-radius: 100px;
  }
  .pricing-name { font-family: 'Playfair Display', serif; font-size: 1.25rem; font-weight: 600; color: var(--navy); }
  .pricing-price { margin: 1rem 0; }
  .pricing-price .amount { font-family: 'Playfair Display', serif; font-size: 2.5rem; font-weight: 700; color: var(--navy); }
  .pricing-price .period { font-size: 0.875rem; color: var(--text-light); }
  .pricing-features { list-style: none; margin: 1.5rem 0; }
  .pricing-features li { font-size: 0.875rem; color: var(--text-mid); padding: 0.4rem 0; display: flex; align-items: center; gap: 0.5rem; }
  .pricing-features li::before { content: '✓'; color: var(--copper); font-weight: 700; }

  /* HOW IT WORKS */
  .steps { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 1.5rem; }
  .step { text-align: center; padding: 1.5rem; }
  .step-number {
    width: 48px; height: 48px; border-radius: 50%;
    background: var(--navy); color: var(--white);
    font-family: 'Playfair Display', serif; font-size: 1.25rem; font-weight: 700;
    display: flex; align-items: center; justify-content: center;
    margin: 0 auto 1rem;
  }
  .step h3 { font-size: 1rem; color: var(--navy); margin-bottom: 0.5rem; }
  .step p { font-size: 0.875rem; color: var(--text-mid); }

  /* EMPTY STATE */
  .empty { text-align: center; padding: 3rem 2rem; }
  .empty-icon { font-size: 3rem; margin-bottom: 1rem; }
  .empty h3 { font-family: 'Playfair Display', serif; color: var(--navy); margin-bottom: 0.5rem; }
  .empty p { color: var(--text-mid); font-size: 0.9rem; margin-bottom: 1.5rem; }

  /* PROPOSAL CARD */
  .proposal-card { border: 1px solid var(--border); border-radius: var(--radius); padding: 1.25rem; background: var(--white); }
  .proposal-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.75rem; }
  .proposal-caterer { font-weight: 600; color: var(--navy); }
  .proposal-price { font-family: 'Playfair Display', serif; font-size: 1.25rem; font-weight: 700; color: var(--copper); }
  .proposal-body { font-size: 0.875rem; color: var(--text-mid); line-height: 1.6; }

  /* FOOTER */
  .footer {
    background: var(--navy); color: rgba(255,255,255,0.6);
    padding: 2.5rem 2rem; text-align: center;
    font-size: 0.8rem;
  }
  .footer a { color: var(--copper-light); text-decoration: none; }

  /* ANIMATIONS */
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
  @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }

  /* UTILS */
  .flex { display: flex; }
  .items-center { align-items: center; }
  .justify-between { justify-content: space-between; }
  .gap-1 { gap: 0.5rem; }
  .gap-2 { gap: 1rem; }
  .mt-1 { margin-top: 0.5rem; }
  .mt-2 { margin-top: 1rem; }
  .mb-2 { margin-bottom: 1rem; }
  .text-sm { font-size: 0.875rem; }
  .text-xs { font-size: 0.75rem; }
  .text-light { color: var(--text-light); }
  .text-navy { color: var(--navy); }
  .font-semibold { font-weight: 600; }
  .w-full { width: 100%; }
  .divider { border: none; border-top: 1px solid var(--border); margin: 1.5rem 0; }

  @media (max-width: 768px) {
    .form-row { grid-template-columns: 1fr; }
    .form-row.cols-3 { grid-template-columns: 1fr; }
    .sidebar { display: none; }
    .hero h1 { font-size: 1.875rem; line-height: 1.15; }
    .hero p { font-size: 0.95rem; }
    .stats-bar { gap: 1.5rem; }
    .nav { padding: 0 0.75rem; gap: 0.5rem; }
    .nav-links { gap: 0.5rem; }
    .nav-btn[style*="opacity: 0.6"] { display: none; }
    .nav-logo { font-size: 1.1rem; }
    .lang-switcher { display: none; }
    .dashboard { display: block; }
    .dash-content { padding: 1rem; }
    .metrics { grid-template-columns: repeat(2, 1fr) !important; gap: 0.5rem; }
    .metric-card { padding: 1rem 0.75rem; }
    .metric-value { font-size: 1.5rem; }
    .metric-label { font-size: 0.7rem; }
    .card-grid.cols-2, .card-grid.cols-3 { grid-template-columns: 1fr; }
    .table-wrap { overflow-x: auto; }
    table { font-size: 0.85rem; }
    .modal { max-width: 100vw; max-height: 100vh; border-radius: 0; padding: 1.25rem; }
    .modal-overlay { padding: 0; }
    .pricing-card { padding: 1.5rem 1.25rem; }
    .pricing-price .amount { font-size: 2rem; }
    .btn { min-height: 44px; }
    .section { padding: 3rem 1rem; }
    .footer { padding: 2rem 1rem; }
  }
`;

// ============================================================
// TRANSLATIONS
// ============================================================
const T = {
  en: {
    tagline: "Catering procurement for",
    tagline2: "Belgian organisations",
    heroSub: "Public sector, private enterprise, hospitals, schools, NGOs. Post a catering brief, receive competitive proposals from vetted Belgian caterers, award contracts — all in one place.",
    postBrief: "Post a Brief",
    joinCaterer: "Join as a Caterer",
    howItWorks: "How It Works",
    pricing: "Pricing",
    login: "Log In",
    register: "Register",
    briefs: "Open Briefs",
    dashboard: "Dashboard",
    myBriefs: "My Briefs",
    proposals: "Proposals",
    caterers: "Caterers",
    settings: "Settings",
    logout: "Log out",
    newBrief: "New Brief",
    viewAll: "View all",
    submit: "Submit",
    cancel: "Cancel",
    save: "Save",
    close: "Close",
    back: "Back",
    // Stats
    activeBriefs: "Active Briefs",
    registeredCaterers: "Registered Caterers",
    contractsAwarded: "Contracts Awarded",
    avgProposals: "Avg. Proposals / Brief",
    // Brief form
    briefTitle: "Brief Title",
    eventType: "Event Type",
    eventDate: "Event Date",
    location: "Location",
    guestCount: "Number of Guests",
    budgetRange: "Budget Range (€)",
    budgetMin: "Min Budget",
    budgetMax: "Max Budget",
    deadline: "Submission Deadline",
    description: "Description & Requirements",
    dietaryReqs: "Dietary Requirements",
    serviceStyle: "Service Style",
    publishBrief: "Publish Brief",
    saveDraft: "Save as Draft",
    // Proposal form
    coverLetter: "Cover Letter",
    menuDescription: "Menu Description",
    totalPrice: "Total Price (€)",
    pricePerPerson: "Price per Person (€)",
    submitProposal: "Submit Proposal",
    // Auth
    email: "Email Address",
    password: "Password",
    orgName: "Organisation Name",
    orgType: "Organisation Type",
    fullName: "Full Name",
    signIn: "Sign In",
    createAccount: "Create Account",
    noAccount: "Don't have an account?",
    haveAccount: "Already have an account?",
    // Event types
    conference: "Conference",
    gala: "Gala Dinner",
    cocktail: "Cocktail Reception",
    working_lunch: "Working Lunch",
    buffet: "Buffet",
    seminar: "Seminar",
    team_event: "Team Event",
    reception: "Reception",
    other: "Other",
    // Org types
    eu_institution: "EU Institution",
    nato: "NATO",
    federal: "Federal Government",
    regional: "Regional Government",
    municipal: "Municipal",
    public_company: "Public Company",
    ngo: "NGO / Association",
    // Service styles
    seated: "Seated Dinner",
    food_stations: "Food Stations",
    mixed: "Mixed",
    // Pricing
    pricingTitle: "Simple, transparent pricing",
    pricingSubtitle: "For organisations posting catering briefs. Caterers join free.",
    perMonth: "/ month",
    mostPopular: "Most Popular",
    getStarted: "Get Started",
    contactSales: "Contact Sales",
  },
  fr: {
    tagline: "L'appel d'offres traiteur pour les",
    tagline2: "organisations belges",
    heroSub: "Secteur public, entreprises, hôpitaux, écoles, ONG. Publiez un cahier des charges, recevez des propositions compétitives de traiteurs vérifiés et attribuez des contrats — le tout sur une seule plateforme.",
    postBrief: "Publier un appel d'offres",
    joinCaterer: "Rejoindre en tant que traiteur",
    howItWorks: "Comment ça marche",
    pricing: "Tarifs",
    login: "Connexion",
    register: "S'inscrire",
    briefs: "Appels d'offres ouverts",
    dashboard: "Tableau de bord",
    myBriefs: "Mes appels d'offres",
    proposals: "Propositions",
    caterers: "Traiteurs",
    settings: "Paramètres",
    logout: "Déconnexion",
    newBrief: "Nouvel appel d'offres",
    viewAll: "Voir tout",
    submit: "Envoyer",
    cancel: "Annuler",
    save: "Enregistrer",
    close: "Fermer",
    back: "Retour",
    activeBriefs: "Appels d'offres actifs",
    registeredCaterers: "Traiteurs inscrits",
    contractsAwarded: "Contrats attribués",
    avgProposals: "Prop. moy. / appel",
    briefTitle: "Titre de l'appel d'offres",
    eventType: "Type d'événement",
    eventDate: "Date de l'événement",
    location: "Lieu",
    guestCount: "Nombre d'invités",
    budgetRange: "Budget (€)",
    budgetMin: "Budget min.",
    budgetMax: "Budget max.",
    deadline: "Date limite de soumission",
    description: "Description et exigences",
    dietaryReqs: "Exigences alimentaires",
    serviceStyle: "Style de service",
    publishBrief: "Publier l'appel d'offres",
    saveDraft: "Enregistrer en brouillon",
    coverLetter: "Lettre de motivation",
    menuDescription: "Description du menu",
    totalPrice: "Prix total (€)",
    pricePerPerson: "Prix par personne (€)",
    submitProposal: "Soumettre la proposition",
    email: "Adresse e-mail",
    password: "Mot de passe",
    orgName: "Nom de l'organisation",
    orgType: "Type d'organisation",
    fullName: "Nom complet",
    signIn: "Se connecter",
    createAccount: "Créer un compte",
    noAccount: "Pas encore de compte ?",
    haveAccount: "Déjà un compte ?",
    conference: "Conférence",
    gala: "Dîner de gala",
    cocktail: "Réception cocktail",
    working_lunch: "Déjeuner de travail",
    buffet: "Buffet",
    seminar: "Séminaire",
    team_event: "Événement d'équipe",
    reception: "Réception",
    other: "Autre",
    eu_institution: "Institution européenne",
    nato: "OTAN",
    federal: "Gouvernement fédéral",
    regional: "Gouvernement régional",
    municipal: "Municipal",
    public_company: "Entreprise publique",
    ngo: "ONG / Association",
    seated: "Dîner assis",
    food_stations: "Buffet à thèmes",
    mixed: "Mixte",
    pricingTitle: "Tarification simple et transparente",
    pricingSubtitle: "Pour les organisations qui publient des appels d'offres. Les traiteurs s'inscrivent gratuitement.",
    perMonth: "/ mois",
    mostPopular: "Plus populaire",
    getStarted: "Commencer",
    contactSales: "Contacter les ventes",
  },
  nl: {
    tagline: "Catering-aanbesteding voor",
    tagline2: "Belgische organisaties",
    heroSub: "Overheid, bedrijven, ziekenhuizen, scholen, NGO's. Plaats een briefing, ontvang concurrerende voorstellen van geverifieerde Belgische cateraars en ken contracten toe — alles op één plek.",
    postBrief: "Opdracht plaatsen",
    joinCaterer: "Aanmelden als cateraar",
    howItWorks: "Hoe het werkt",
    pricing: "Prijzen",
    login: "Inloggen",
    register: "Registreren",
    briefs: "Openstaande opdrachten",
    dashboard: "Dashboard",
    myBriefs: "Mijn opdrachten",
    proposals: "Offertes",
    caterers: "Cateraars",
    settings: "Instellingen",
    logout: "Uitloggen",
    newBrief: "Nieuwe opdracht",
    viewAll: "Alles bekijken",
    submit: "Indienen",
    cancel: "Annuleren",
    save: "Opslaan",
    close: "Sluiten",
    back: "Terug",
    activeBriefs: "Actieve opdrachten",
    registeredCaterers: "Geregistreerde cateraars",
    contractsAwarded: "Toegewezen contracten",
    avgProposals: "Gem. offertes / opdracht",
    briefTitle: "Titel van de opdracht",
    eventType: "Type evenement",
    eventDate: "Datum evenement",
    location: "Locatie",
    guestCount: "Aantal gasten",
    budgetRange: "Budget (€)",
    budgetMin: "Min. budget",
    budgetMax: "Max. budget",
    deadline: "Indieningsdeadline",
    description: "Beschrijving en vereisten",
    dietaryReqs: "Voedingsvereisten",
    serviceStyle: "Servicestijl",
    publishBrief: "Opdracht publiceren",
    saveDraft: "Opslaan als concept",
    coverLetter: "Begeleidende brief",
    menuDescription: "Menubeschrijving",
    totalPrice: "Totaalprijs (€)",
    pricePerPerson: "Prijs per persoon (€)",
    submitProposal: "Offerte indienen",
    email: "E-mailadres",
    password: "Wachtwoord",
    orgName: "Naam organisatie",
    orgType: "Type organisatie",
    fullName: "Volledige naam",
    signIn: "Inloggen",
    createAccount: "Account aanmaken",
    noAccount: "Nog geen account?",
    haveAccount: "Al een account?",
    conference: "Conferentie",
    gala: "Galadiner",
    cocktail: "Cocktailreceptie",
    working_lunch: "Werklunch",
    buffet: "Buffet",
    seminar: "Seminar",
    team_event: "Teamactiviteit",
    reception: "Receptie",
    other: "Overige",
    eu_institution: "EU-instelling",
    nato: "NAVO",
    federal: "Federale overheid",
    regional: "Gewestelijke overheid",
    municipal: "Gemeentelijk",
    public_company: "Overheidsbedrijf",
    ngo: "NGO / Vereniging",
    seated: "Zitdiner",
    food_stations: "Foodstations",
    mixed: "Gemengd",
    pricingTitle: "Eenvoudige, transparante prijzen",
    pricingSubtitle: "Voor organisaties die cateringopdrachten plaatsen. Cateraars melden zich gratis aan.",
    perMonth: "/ maand",
    mostPopular: "Meest populair",
    getStarted: "Aan de slag",
    contactSales: "Contact verkoop",
  }
};

// ============================================================
// CONTEXT
// ============================================================
const AppContext = createContext();
const useApp = () => useContext(AppContext);

// ============================================================
// MOCK DATA
// ============================================================
const MOCK_BRIEFS = [
  {
    id: "1", reference_number: "CB-2026-00001",
    title_en: "Annual Staff Reception — EU Commission DG AGRI",
    title_fr: "Réception annuelle du personnel — DG AGRI",
    title_nl: "Jaarlijkse personeelsreceptie — DG AGRI",
    event_type: "reception", event_date: "2026-05-15",
    location: "Rue de la Loi 130, Brussels", guest_count: 280,
    budget_min: 8000, budget_max: 14000, deadline: "2026-04-10",
    status: "open", service_style: "cocktail",
    description_en: "Annual staff reception for DG AGRI. Cocktail format, standing, with finger foods and canapés. Must accommodate dietary requirements. Local and seasonal produce preferred. Full service including staff.",
    org: "European Commission — DG AGRI",
    proposal_count: 4
  },
  {
    id: "2", reference_number: "CB-2026-00002",
    title_en: "Ministerial Working Lunch — Belgian Federal Government",
    title_fr: "Déjeuner ministériel — Gouvernement fédéral belge",
    title_nl: "Ministeriële werklunch — Belgische federale overheid",
    event_type: "working_lunch", event_date: "2026-04-22",
    location: "Rue de la Loi 16, Brussels", guest_count: 24,
    budget_min: 2500, budget_max: 4500, deadline: "2026-04-05",
    status: "open", service_style: "seated",
    description_en: "Formal working lunch for ministerial delegation. Seated service, 3 courses. High presentation standards required. Halal options required for 8 guests.",
    org: "Belgian Federal Government",
    proposal_count: 7
  },
  {
    id: "3", reference_number: "CB-2026-00003",
    title_en: "NATO Summit Side Event Catering",
    title_fr: "Restauration événement parallèle Sommet OTAN",
    title_nl: "Catering zijdelings evenement NAVO-top",
    event_type: "conference", event_date: "2026-06-10",
    location: "NATO HQ, Boulevard Léopold III, Brussels", guest_count: 150,
    budget_min: 15000, budget_max: 25000, deadline: "2026-04-30",
    status: "open", service_style: "buffet",
    description_en: "Catering for side event during NATO summit week. Buffet lunch and coffee breaks across a full conference day. Security clearance required for all catering staff. International dietary requirements.",
    org: "NATO Headquarters",
    proposal_count: 2
  },
  {
    id: "4", reference_number: "CB-2026-00004",
    title_en: "City of Brussels Annual Citizenship Ceremony",
    title_fr: "Cérémonie annuelle de citoyenneté de la Ville de Bruxelles",
    title_nl: "Jaarlijkse burgerreceptie Stad Brussel",
    event_type: "gala", event_date: "2026-05-30",
    location: "Hôtel de Ville, Grand Place, Brussels", guest_count: 400,
    budget_min: 20000, budget_max: 35000, deadline: "2026-04-20",
    status: "open", service_style: "mixed",
    description_en: "Prestigious annual citizenship ceremony at the Grand Place. Full gala service including welcome drinks, dinner service and dessert reception. Must respect heritage venue constraints.",
    org: "City of Brussels",
    proposal_count: 6
  }
];

const MOCK_CATERERS = [
  { id: "1", company_name: "Artfood Traiteur", specialties: ["gala", "corporate", "cocktail"], verification_status: "approved", price_range: "premium", avg_rating: 4.8, description_en: "Brussels premier event caterer since 1987. Specialists in institutional and high-protocol events." },
  { id: "2", company_name: "KTCHN Brussels", specialties: ["conference", "buffet", "corporate"], verification_status: "approved", price_range: "mid", avg_rating: 4.6, description_en: "Modern event catering with sustainable focus. Specialists in large-scale professional events and trade fairs." },
  { id: "3", company_name: "Traiteur Durable", specialties: ["sustainable", "cocktail", "buffet"], verification_status: "approved", price_range: "mid", avg_rating: 4.7, description_en: "Fully eco-certified caterer. Seasonal, local, zero-waste menus for events of all sizes." },
  { id: "4", company_name: "Le Grand Buffet", specialties: ["gala", "reception", "seated"], verification_status: "pending", price_range: "luxury", avg_rating: null, description_en: "Fine dining catering specialists for prestigious institutional events across Brussels." },
];

// ============================================================
// COMPONENTS
// ============================================================

function Modal({ title, children, footer, onClose, size = "md" }) {
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: size === "lg" ? "820px" : "640px" }}>
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

function BriefCard({ brief, onClick, lang = "en" }) {
  const title = brief[`title_${lang}`] || brief.title_en;
  return (
    <div className="card brief-card" onClick={() => onClick(brief)}>
      <div className="brief-header">
        <div>
          <div className="brief-title">{title}</div>
          <div className="brief-org">{brief.org} · {brief.reference_number}</div>
        </div>
        <span className={`badge badge-${brief.status}`}>{brief.status}</span>
      </div>
      <div className="brief-meta">
        <span className="brief-meta-item">📅 {brief.event_date}</span>
        <span className="brief-meta-item">📍 {brief.location.split(",")[0]}</span>
        <span className="brief-meta-item">👥 {brief.guest_count} guests</span>
        <span className="brief-meta-item">🍽 {brief.event_type?.replace("_", " ")}</span>
      </div>
      <div className="brief-footer">
        <div className="brief-budget">
          €{brief.budget_min?.toLocaleString()} – €{brief.budget_max?.toLocaleString()}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <span style={{ fontSize: "0.75rem", color: "var(--text-light)" }}>
            {brief.proposal_count} proposal{brief.proposal_count !== 1 ? "s" : ""}
          </span>
          <span className="brief-deadline">Deadline: {brief.deadline}</span>
        </div>
      </div>
    </div>
  );
}

function BriefDetail({ brief, user, lang, onClose, onPropose }) {
  const title = brief[`title_${lang}`] || brief.title_en;
  const desc = brief[`description_${lang}`] || brief.description_en;
  const t = T[lang];
  return (
    <Modal title={title} onClose={onClose} size="lg"
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>{t.close}</button>
          {user?.role === "caterer" && brief.status === "open" && (
            <button className="btn btn-primary" onClick={() => onPropose(brief)}>
              Submit Proposal
            </button>
          )}
          {user?.role === "buyer" && (
            <button className="btn btn-secondary">View {brief.proposal_count} Proposals</button>
          )}
        </>
      }
    >
      <div className="flex items-center gap-1 mb-2" style={{ flexWrap: "wrap" }}>
        <span className={`badge badge-${brief.status}`}>{brief.status}</span>
        <span className="badge" style={{ background: "var(--sand-dark)", color: "var(--text-mid)" }}>{brief.event_type?.replace("_", " ")}</span>
        <span className="badge" style={{ background: "var(--sand-dark)", color: "var(--text-mid)" }}>{brief.service_style}</span>
      </div>
      <div className="form-row" style={{ marginBottom: "1.25rem" }}>
        <div><div className="form-label">Organisation</div><div className="text-sm">{brief.org}</div></div>
        <div><div className="form-label">Reference</div><div className="text-sm">{brief.reference_number}</div></div>
        <div><div className="form-label">Event Date</div><div className="text-sm">{brief.event_date}</div></div>
        <div><div className="form-label">Deadline</div><div className="text-sm font-semibold" style={{ color: "var(--error)" }}>{brief.deadline}</div></div>
        <div><div className="form-label">Location</div><div className="text-sm">{brief.location}</div></div>
        <div><div className="form-label">Guests</div><div className="text-sm">{brief.guest_count}</div></div>
      </div>
      <div className="form-group">
        <div className="form-label">Budget Range</div>
        <div className="text-sm font-semibold" style={{ color: "var(--navy)", fontSize: "1.1rem" }}>
          €{brief.budget_min?.toLocaleString()} – €{brief.budget_max?.toLocaleString()}
        </div>
      </div>
      <div className="form-group">
        <div className="form-label">Description & Requirements</div>
        <div className="text-sm" style={{ lineHeight: "1.7", color: "var(--text-mid)" }}>{desc}</div>
      </div>
    </Modal>
  );
}

function BriefForm({ onClose, onSubmit, lang }) {
  const t = T[lang];
  const [form, setForm] = useState({
    title_en: "", event_type: "", event_date: "", location: "Brussels",
    guest_count: "", budget_min: "", budget_max: "", deadline: "",
    description_en: "", service_style: "", dietary_requirements: []
  });
  const dietary = ["vegetarian", "vegan", "halal", "kosher", "gluten_free"];
  const toggleDietary = (d) => {
    setForm(f => ({
      ...f, dietary_requirements: f.dietary_requirements.includes(d)
        ? f.dietary_requirements.filter(x => x !== d)
        : [...f.dietary_requirements, d]
    }));
  };
  return (
    <Modal title={t.newBrief} onClose={onClose} size="lg"
      footer={
        <>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>{t.cancel}</button>
          <button className="btn btn-ghost btn-sm" onClick={() => onSubmit({ ...form, status: "draft" })}>{t.saveDraft}</button>
          <button className="btn btn-primary btn-sm" onClick={() => onSubmit({ ...form, status: "open" })}>{t.publishBrief}</button>
        </>
      }
    >
      <div className="form-group">
        <label className="form-label required">{t.briefTitle}</label>
        <input className="form-input" placeholder="e.g. Annual Staff Reception — DG TRADE" value={form.title_en} onChange={e => setForm(f => ({ ...f, title_en: e.target.value }))} />
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label required">{t.eventType}</label>
          <select className="form-select" value={form.event_type} onChange={e => setForm(f => ({ ...f, event_type: e.target.value }))}>
            <option value="">Select type...</option>
            {["conference","gala","cocktail","working_lunch","buffet","seminar","team_event","reception","other"].map(v => (
              <option key={v} value={v}>{t[v] || v}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label required">{t.serviceStyle}</label>
          <select className="form-select" value={form.service_style} onChange={e => setForm(f => ({ ...f, service_style: e.target.value }))}>
            <option value="">Select style...</option>
            {["seated","buffet","cocktail","food_stations","mixed"].map(v => (
              <option key={v} value={v}>{t[v] || v}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label required">{t.eventDate}</label>
          <input className="form-input" type="date" value={form.event_date} onChange={e => setForm(f => ({ ...f, event_date: e.target.value }))} />
        </div>
        <div className="form-group">
          <label className="form-label required">{t.deadline}</label>
          <input className="form-input" type="date" value={form.deadline} onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))} />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label required">{t.location}</label>
          <input className="form-input" value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} />
        </div>
        <div className="form-group">
          <label className="form-label required">{t.guestCount}</label>
          <input className="form-input" type="number" min="1" value={form.guest_count} onChange={e => setForm(f => ({ ...f, guest_count: e.target.value }))} />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">{t.budgetMin}</label>
          <input className="form-input" type="number" placeholder="5000" value={form.budget_min} onChange={e => setForm(f => ({ ...f, budget_min: e.target.value }))} />
        </div>
        <div className="form-group">
          <label className="form-label">{t.budgetMax}</label>
          <input className="form-input" type="number" placeholder="15000" value={form.budget_max} onChange={e => setForm(f => ({ ...f, budget_max: e.target.value }))} />
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">{t.dietaryReqs}</label>
        <div className="checkbox-group">
          {dietary.map(d => (
            <div className="checkbox-pill" key={d}>
              <input type="checkbox" id={d} checked={form.dietary_requirements.includes(d)} onChange={() => toggleDietary(d)} />
              <label htmlFor={d}>{d.replace("_", " ")}</label>
            </div>
          ))}
        </div>
      </div>
      <div className="form-group">
        <label className="form-label required">{t.description}</label>
        <textarea className="form-textarea" rows={4} placeholder="Describe the event, specific requirements, service expectations, any constraints..." value={form.description_en} onChange={e => setForm(f => ({ ...f, description_en: e.target.value }))} />
      </div>
    </Modal>
  );
}

function ProposalForm({ brief, onClose, onSubmit, lang }) {
  const t = T[lang];
  const [form, setForm] = useState({ cover_letter_en: "", menu_description: "", price_total: "", price_per_person: "", additional_notes: "" });
  return (
    <Modal title={`Submit Proposal — ${brief.title_en}`} onClose={onClose} size="lg"
      footer={
        <>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>{t.cancel}</button>
          <button className="btn btn-primary btn-sm" onClick={() => onSubmit(form)}>{t.submitProposal}</button>
        </>
      }
    >
      <div className="alert alert-info">
        Responding to: <strong>{brief.org}</strong> · Budget: €{brief.budget_min?.toLocaleString()} – €{brief.budget_max?.toLocaleString()} · Deadline: {brief.deadline}
      </div>
      <div className="form-group">
        <label className="form-label required">{t.coverLetter}</label>
        <textarea className="form-textarea" rows={4} placeholder="Introduce your company, why you're the right fit for this event..." value={form.cover_letter_en} onChange={e => setForm(f => ({ ...f, cover_letter_en: e.target.value }))} />
      </div>
      <div className="form-group">
        <label className="form-label required">{t.menuDescription}</label>
        <textarea className="form-textarea" rows={5} placeholder="Describe your proposed menu in detail. Include starter, main, dessert, beverages. Note how you address dietary requirements." value={form.menu_description} onChange={e => setForm(f => ({ ...f, menu_description: e.target.value }))} />
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label required">{t.totalPrice}</label>
          <input className="form-input" type="number" placeholder="12000" value={form.price_total} onChange={e => setForm(f => ({ ...f, price_total: e.target.value }))} />
        </div>
        <div className="form-group">
          <label className="form-label">{t.pricePerPerson} <span className="text-xs text-light">(optional)</span></label>
          <input className="form-input" type="number" placeholder="48" value={form.price_per_person} onChange={e => setForm(f => ({ ...f, price_per_person: e.target.value }))} />
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">Additional Notes</label>
        <textarea className="form-textarea" rows={3} placeholder="Certifications, references, setup requirements, staffing details..." value={form.additional_notes} onChange={e => setForm(f => ({ ...f, additional_notes: e.target.value }))} />
      </div>
    </Modal>
  );
}

function AuthModal({ mode, onClose, onAuth, lang, error, busy }) {
  const t = T[lang];
  const [isLogin, setIsLogin] = useState(mode === "login");
  const [role, setRole] = useState("buyer");
  const [form, setForm] = useState({ email: "", password: "", full_name: "", organisation: "", org_type: "", vat_number: "" });
  const [viesResult, setViesResult] = useState(null);
  const [viesBusy, setViesBusy] = useState(false);

  useEffect(() => {
    if (role !== "caterer" || isLogin || !form.vat_number || form.vat_number.length < 8) {
      setViesResult(null);
      return;
    }
    setViesBusy(true);
    const handle = setTimeout(async () => {
      const r = await checkVat(form.vat_number);
      setViesResult(r);
      setViesBusy(false);
      // Auto-fill company name from VIES if user hasn't typed one yet
      if (r?.valid && r.name && !form.organisation) {
        setForm(f => ({ ...f, organisation: r.name }));
      }
    }, 500);
    return () => { clearTimeout(handle); setViesBusy(false); };
  }, [form.vat_number, role, isLogin]);

  return (
    <Modal title={isLogin ? t.signIn : t.createAccount} onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={busy}>{t.cancel}</button>
          <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => onAuth({ ...form, role, mode: isLogin ? "login" : "register" })}>
            {busy ? "..." : (isLogin ? t.signIn : t.createAccount)}
          </button>
        </>
      }
    >
      {error && <div className="alert alert-error" style={{ marginBottom: "1rem" }}>{error}</div>}
      {!isLogin && (
        <div className="form-group">
          <label className="form-label">I am a...</label>
          <div style={{ display: "flex", gap: "0.75rem" }}>
            {["buyer", "caterer"].map(r => (
              <button key={r} className={`btn ${role === r ? "btn-secondary" : "btn-ghost"} btn-sm`} onClick={() => setRole(r)}>
                {r === "buyer" ? "🏛 Buying Organisation" : "🍽 Catering Company"}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="form-group">
        <label className="form-label required">{t.email}</label>
        <input className="form-input" type="email" placeholder="you@organisation.be" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
      </div>
      <div className="form-group">
        <label className="form-label required">{t.password}</label>
        <input className="form-input" type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
      </div>
      {!isLogin && (
        <>
          <div className="form-group">
            <label className="form-label required">{t.fullName}</label>
            <input className="form-input" value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label required">{role === "buyer" ? t.orgName : "Company Name"}</label>
            <input className="form-input" value={form.organisation} onChange={e => setForm(f => ({ ...f, organisation: e.target.value }))} />
          </div>
          {role === "buyer" && (
            <div className="form-group">
              <label className="form-label">{t.orgType}</label>
              <select className="form-select" value={form.org_type} onChange={e => setForm(f => ({ ...f, org_type: e.target.value }))}>
                <option value="">Select type...</option>
                {["eu_institution","nato","federal","regional","municipal","public_company","ngo"].map(v => (
                  <option key={v} value={v}>{t[v] || v}</option>
                ))}
              </select>
            </div>
          )}
          {role === "caterer" && (
            <>
              <div className="form-group">
                <label className="form-label required">VAT number</label>
                <input className="form-input" placeholder="BE0123456789" value={form.vat_number} onChange={e => setForm(f => ({ ...f, vat_number: e.target.value }))} />
                {viesBusy && <div className="text-xs text-light" style={{ marginTop: "0.25rem" }}>Checking VIES…</div>}
                {viesResult?.valid === true && (
                  <div className="text-xs" style={{ marginTop: "0.25rem", color: "var(--success)" }}>
                    ✓ Valid VAT · {viesResult.name || "registered"}
                  </div>
                )}
                {viesResult?.valid === false && (
                  <div className="text-xs" style={{ marginTop: "0.25rem", color: "var(--error)" }}>
                    ✗ Not found in EU VIES register
                  </div>
                )}
              </div>
              <div className="alert alert-info">
                Catering companies join free. We verify your VAT number against the EU VIES register and the Belgian BCE/KBO. Approval typically within 24 hours.
              </div>
            </>
          )}
          {role === "buyer" && (
            <div className="alert alert-info">
              After registration, choose your subscription plan to start posting catering briefs.
            </div>
          )}
        </>
      )}
      <div className="text-sm text-light mt-1">
        {isLogin ? t.noAccount : t.haveAccount}{" "}
        <button style={{ background: "none", border: "none", color: "var(--copper)", cursor: "pointer", fontWeight: 600, fontSize: "inherit" }} onClick={() => setIsLogin(!isLogin)}>
          {isLogin ? t.createAccount : t.signIn}
        </button>
      </div>
    </Modal>
  );
}

// ============================================================
// PAGES
// ============================================================

function LandingPage({ onNav, lang, onAuth }) {
  // Coming-soon mode — full landing preserved as LandingPageFull below for easy revert
  const [email, setEmail] = useState("");
  const [roleHint, setRoleHint] = useState("buyer");
  const [organisation, setOrganisation] = useState("");
  const [status, setStatus] = useState(null); // null | "submitting" | "done" | "error"
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus("submitting");
    setError(null);
    try {
      await signUpForInterest({ email, locale: lang, role_hint: roleHint, organisation: organisation || null });
      setStatus("done");
    } catch (err) {
      setError(err.message || "Something went wrong.");
      setStatus("error");
    }
  };

  return (
    <div>
      <div className="hero" style={{ minHeight: "calc(100vh - 64px - 80px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div className="hero-content" style={{ textAlign: "center", maxWidth: "640px" }}>
          <div className="hero-badge">🇧🇪 Coming soon</div>
          <h1 style={{ fontFamily: "Playfair Display, serif", color: "var(--white)" }}>
            Catering procurement for <em>Belgian organisations</em>
          </h1>
          <p style={{ color: "rgba(255,255,255,0.7)", fontSize: "1.05rem", lineHeight: 1.6, margin: "1.5rem 0 2rem" }}>
            Caterbase is launching soon — a structured way for Belgian organisations to brief catering needs and receive proposals from vetted caterers, all in one place.
          </p>

          {status !== "done" ? (
            <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: "0.75rem", maxWidth: "420px", margin: "0 auto" }}>
              <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center", marginBottom: "0.25rem" }}>
                {[
                  { id: "buyer", label: "I'm a buyer" },
                  { id: "caterer", label: "I'm a caterer" },
                  { id: "other", label: "Other" },
                ].map(opt => (
                  <button
                    type="button"
                    key={opt.id}
                    onClick={() => setRoleHint(opt.id)}
                    style={{
                      background: roleHint === opt.id ? "var(--copper)" : "rgba(255,255,255,0.08)",
                      color: roleHint === opt.id ? "white" : "rgba(255,255,255,0.7)",
                      border: "1px solid rgba(255,255,255,0.15)",
                      borderRadius: "999px",
                      padding: "0.4rem 0.9rem",
                      fontSize: "0.8rem",
                      cursor: "pointer",
                      fontWeight: 500,
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <input
                type="email"
                placeholder="you@organisation.be"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                style={{ padding: "0.85rem 1rem", borderRadius: "8px", border: "1px solid rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.08)", color: "white", fontSize: "0.95rem" }}
              />
              <input
                type="text"
                placeholder="Organisation (optional)"
                value={organisation}
                onChange={e => setOrganisation(e.target.value)}
                style={{ padding: "0.85rem 1rem", borderRadius: "8px", border: "1px solid rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.08)", color: "white", fontSize: "0.95rem" }}
              />
              <button type="submit" className="btn btn-primary btn-lg" disabled={status === "submitting"}>
                {status === "submitting" ? "Adding you…" : "Request early access"}
              </button>
              {error && <div style={{ color: "#ffb4a8", fontSize: "0.85rem", marginTop: "0.25rem" }}>{error}</div>}
            </form>
          ) : (
            <div style={{ background: "rgba(196,119,58,0.15)", border: "1px solid rgba(196,119,58,0.4)", padding: "1.25rem", borderRadius: "8px", color: "white", maxWidth: "420px", margin: "0 auto" }}>
              <strong>You're on the list.</strong>
              <div style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.9rem", marginTop: "0.5rem" }}>
                We'll email you the moment Caterbase opens for sign-ups.
              </div>
            </div>
          )}

          <p style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.8rem", marginTop: "2rem" }}>
            Built in Brussels · Belgium · <a href="mailto:support@caterbase.be" style={{ color: "rgba(255,255,255,0.6)" }}>support@caterbase.be</a>
          </p>
        </div>
      </div>
    </div>
  );
}

function LandingPageFull({ onNav, lang, onAuth }) {
  const t = T[lang];
  const [liveBriefs, setLiveBriefs] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetchOpenBriefs({ limit: 4 })
      .then(rows => { if (!cancelled) setLiveBriefs(rows || []); })
      .catch(() => { if (!cancelled) setLiveBriefs([]); });
    return () => { cancelled = true; };
  }, []);
  return (
    <div>
      {/* HERO */}
      <div className="hero">
        <div className="hero-content">
          <div className="hero-badge">🇧🇪 Public sector · Enterprise · Hospitals · NGOs</div>
          <h1>{t.tagline} <em>{t.tagline2}</em></h1>
          <p>{t.heroSub}</p>
          <div className="hero-ctas">
            <button className="btn btn-primary btn-lg" onClick={() => onAuth("register")}>
              {t.postBrief}
            </button>
            <button className="btn btn-outline btn-lg" style={{ color: "white", borderColor: "rgba(255,255,255,0.4)" }} onClick={() => onAuth("register")}>
              {t.joinCaterer}
            </button>
          </div>
        </div>
      </div>

      {/* STATS */}
      <div className="stats-bar">
        {[
          { n: "127", l: "Active Briefs" },
          { n: "84", l: "Verified Caterers" },
          { n: "€4.2M", l: "Contracts Awarded" },
          { n: "6.3", l: "Avg. Proposals / Brief" },
        ].map(s => (
          <div className="stat" key={s.l}>
            <span className="stat-number">{s.n}</span>
            <span className="stat-label">{s.l}</span>
          </div>
        ))}
      </div>

      {/* OPEN BRIEFS PREVIEW — only renders if there are real open briefs */}
      {liveBriefs && liveBriefs.length > 0 && (
        <div className="section" style={{ background: "var(--white)" }}>
          <div className="section-inner">
            <div className="section-header">
              <h2>Recent Open Briefs</h2>
              <p>Active catering requirements from Brussels institutions</p>
            </div>
            <div className="card-grid cols-2">
              {liveBriefs.map(b => (
                <BriefCard key={b.id} brief={b} lang={lang} onClick={() => onAuth("register")} />
              ))}
            </div>
            <div style={{ textAlign: "center", marginTop: "2rem" }}>
              <button className="btn btn-outline" onClick={() => onAuth("register")}>
                View all open briefs →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* HOW IT WORKS */}
      <div className="section">
        <div className="section-inner">
          <div className="section-header">
            <h2>{t.howItWorks}</h2>
            <p>Streamlined procurement from brief to award</p>
          </div>
          <div className="steps">
            {[
              { n: "1", title: "Post your brief", desc: "Describe your event, requirements, budget and deadline. Takes 5 minutes." },
              { n: "2", title: "Receive proposals", desc: "Verified catering companies submit full proposals with menus, pricing and credentials." },
              { n: "3", title: "Compare & select", desc: "Review all proposals side by side. Message caterers directly with questions." },
              { n: "4", title: "Award the contract", desc: "Award your brief to the winning caterer. We notify all parties automatically." },
            ].map(s => (
              <div className="step" key={s.n}>
                <div className="step-number">{s.n}</div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* PRICING */}
      <div className="section" style={{ background: "var(--navy)", paddingBottom: "5rem" }}>
        <div className="section-inner">
          <div className="section-header">
            <h2 style={{ color: "var(--white)" }}>{t.pricingTitle}</h2>
            <p style={{ color: "rgba(255,255,255,0.6)" }}>{t.pricingSubtitle}</p>
          </div>
          <div className="pricing-grid" style={{ maxWidth: "480px" }}>
            <div className="pricing-card featured">
              <div className="pricing-name">Starter</div>
              <div className="pricing-price">
                <span className="amount">€149</span>
                <span className="period">{t.perMonth}</span>
              </div>
              <div className="text-sm" style={{ color: "var(--copper)", fontWeight: 600, marginBottom: "1rem" }}>Unlimited briefs</div>
              <ul className="pricing-features">
                <li>Unlimited brief postings</li>
                <li>Full proposal management</li>
                <li>Direct messaging with caterers</li>
                <li>Proposal comparison + PDF export</li>
                <li>Full audit trail for procurement</li>
                <li>Email support</li>
              </ul>
              <button className="btn btn-primary w-full" onClick={() => onAuth("register")}>{t.getStarted}</button>
            </div>
          </div>
          <div style={{ textAlign: "center", marginTop: "2rem", color: "rgba(255,255,255,0.5)", fontSize: "0.875rem" }}>
            Catering companies always join free. No commission on contracts.
          </div>
        </div>
      </div>
    </div>
  );
}

function PlanGate({ onChoose }) {
  return (
    <div className="card" style={{ background: "var(--sand-dark)", marginBottom: "1.5rem", padding: "1.5rem" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "1rem" }}>
        <div>
          <h3 style={{ fontFamily: "Playfair Display, serif", color: "var(--navy)", marginBottom: "0.25rem" }}>Activate your subscription to start posting briefs</h3>
          <p style={{ color: "var(--text-mid)", fontSize: "0.875rem", marginBottom: "0.75rem" }}>
            One flat price. Unlimited briefs. Caterers join free, no commission.
          </p>
          <ul style={{ margin: 0, paddingLeft: "1.2rem", color: "var(--text-mid)", fontSize: "0.875rem", lineHeight: "1.7" }}>
            <li>Unlimited brief postings</li>
            <li>Direct messaging with verified caterers</li>
            <li>Proposal comparison + PDF export</li>
            <li>Full audit trail for procurement compliance</li>
          </ul>
        </div>
        <div style={{ textAlign: "right", minWidth: "200px" }}>
          <div style={{ fontSize: "2.25rem", fontWeight: 700, color: "var(--navy)", fontFamily: "Playfair Display, serif" }}>€149<span style={{ fontSize: "0.85rem", color: "var(--text-light)", fontWeight: 400 }}> / month</span></div>
          <button className="btn btn-primary" style={{ marginTop: "0.75rem" }} onClick={() => onChoose("starter")}>
            Subscribe
          </button>
        </div>
      </div>
    </div>
  );
}

function BuyerDashboard({ user, lang, onLogout }) {
  const t = T[lang];
  const [view, setView] = useState("overview");
  const [showBriefForm, setShowBriefForm] = useState(false);
  const [selectedBrief, setSelectedBrief] = useState(null);
  const [briefs, setBriefs] = useState(user.__demo ? MOCK_BRIEFS.slice(0, 3) : []);
  const [loading, setLoading] = useState(!user.__demo);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    if (user.__demo) return;
    let cancelled = false;
    setLoading(true);
    fetchMyBriefs()
      .then(rows => { if (!cancelled) { setBriefs(rows); setLoading(false); } })
      .catch(err => { if (!cancelled) { showToast(err.message, "error"); setLoading(false); } });
    return () => { cancelled = true; };
  }, [user.__demo]);

  const handleNewBrief = async (data) => {
    if (user.__demo) {
      const newBrief = {
        ...data,
        id: Date.now().toString(),
        reference_number: `CB-2026-${String(briefs.length + 10).padStart(5, "0")}`,
        org: user.organisation,
        proposal_count: 0
      };
      setBriefs(b => [newBrief, ...b]);
      setShowBriefForm(false);
      showToast(data.status === "open" ? "Brief published successfully!" : "Draft saved.");
      return;
    }
    try {
      const row = await insertBrief(data);
      setBriefs(b => [row, ...b]);
      setShowBriefForm(false);
      showToast(data.status === "open" ? "Brief published successfully!" : "Draft saved.");
    } catch (err) {
      showToast(err.message || "Failed to publish brief", "error");
    }
  };

  return (
    <div className="dashboard">
      <div className="sidebar">
        <div style={{ padding: "1rem 1.25rem 0.5rem", borderBottom: "1px solid var(--border)", marginBottom: "1rem" }}>
          <div style={{ fontWeight: 700, fontSize: "0.875rem", color: "var(--navy)" }}>{user.organisation}</div>
          <div style={{ fontSize: "0.75rem", color: "var(--text-light)" }}>{user.email}</div>
          <div style={{ marginTop: "0.5rem" }}>
            <span className={`badge badge-${user.subscription_status === 'active' ? 'approved' : 'pending'}`}>
              {user.subscription_status === 'active' ? `${user.subscription_plan || 'Active'} plan` : (user.subscription_status || 'No subscription')}
            </span>
          </div>
          {!user.__demo && user.subscription_status === 'active' && (
            <button className="btn btn-ghost btn-sm" style={{ marginTop: "0.5rem", fontSize: "0.75rem" }} onClick={async () => {
              try { await openPortal(); } catch (e) { showToast(e.message, "error"); }
            }}>Manage subscription →</button>
          )}
        </div>
        {[
          { id: "overview", icon: "◎", label: "Overview" },
          { id: "briefs", icon: "📋", label: t.myBriefs, badge: briefs.length },
          { id: "proposals", icon: "📬", label: t.proposals, badge: 11 },
          { id: "caterers", icon: "🍽", label: t.caterers },
          { id: "settings", icon: "⚙", label: t.settings },
        ].map(item => (
          <div key={item.id} className={`sidebar-item ${view === item.id ? "active" : ""}`} onClick={() => setView(item.id)}>
            <span className="sidebar-icon">{item.icon}</span>
            {item.label}
            {item.badge && <span className="sidebar-badge">{item.badge}</span>}
          </div>
        ))}
        <div className="sidebar-item" style={{ marginTop: "auto", color: "var(--error)" }} onClick={onLogout}>
          <span className="sidebar-icon">↩</span> {t.logout}
        </div>
      </div>

      <div className="dash-content">
        {toast && <div className={`alert alert-${toast.type}`}>{toast.msg}</div>}

        {!user.__demo && user.subscription_status !== "active" && (
          <PlanGate onChoose={async (planId) => {
            try { await startCheckout(planId); }
            catch (e) { showToast(e.message, "error"); }
          }} />
        )}

        {view === "overview" && (
          <>
            <div className="dash-header">
              <h2>Welcome back, {user.full_name?.split(" ")[0]}</h2>
              <p>Here's what's happening with your catering procurement</p>
            </div>
            <div className="metrics">
              {[
                { v: briefs.filter(b => b.status === "open").length, l: "Active Briefs" },
                { v: 11, l: "Total Proposals Received" },
                { v: 2, l: "Contracts Awarded" },
                { v: "€47.2k", l: "Total Contract Value" },
              ].map(m => (
                <div className="metric-card" key={m.l}>
                  <div className="metric-value">{m.v}</div>
                  <div className="metric-label">{m.l}</div>
                </div>
              ))}
            </div>
            <div className="flex justify-between items-center mb-2">
              <h3 style={{ fontFamily: "Playfair Display, serif", color: "var(--navy)" }}>Recent Briefs</h3>
              <button className="btn btn-primary btn-sm" onClick={() => setShowBriefForm(true)}>+ {t.newBrief}</button>
            </div>
            <div className="card-grid cols-2">
              {briefs.slice(0, 4).map(b => (
                <BriefCard key={b.id} brief={b} lang={lang} onClick={setSelectedBrief} />
              ))}
            </div>
          </>
        )}

        {view === "briefs" && (
          <>
            <div className="flex justify-between items-center mb-2">
              <div className="dash-header" style={{ marginBottom: 0 }}>
                <h2>{t.myBriefs}</h2>
                <p>Manage your catering briefs and track proposals</p>
              </div>
              <button className="btn btn-primary" onClick={() => setShowBriefForm(true)}>+ {t.newBrief}</button>
            </div>
            <div className="card-grid cols-2" style={{ marginTop: "1.5rem" }}>
              {briefs.map(b => (
                <BriefCard key={b.id} brief={b} lang={lang} onClick={setSelectedBrief} />
              ))}
            </div>
            {briefs.length === 0 && (
              <div className="empty">
                <div className="empty-icon">📋</div>
                <h3>No briefs yet</h3>
                <p>Post your first catering brief to start receiving proposals</p>
                <button className="btn btn-primary" onClick={() => setShowBriefForm(true)}>Post a Brief</button>
              </div>
            )}
          </>
        )}

        {view === "proposals" && (
          <>
            <div className="dash-header">
              <h2>{t.proposals}</h2>
              <p>All proposals received across your active briefs</p>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              {MOCK_BRIEFS.slice(0, 2).map(brief => (
                <div key={brief.id}>
                  <div className="flex items-center gap-1 mb-1">
                    <h3 style={{ fontFamily: "Playfair Display, serif", color: "var(--navy)", fontSize: "1rem" }}>{brief.title_en}</h3>
                    <span className={`badge badge-${brief.status}`}>{brief.status}</span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                    {MOCK_CATERERS.filter(c => c.verification_status === "approved").map(cat => (
                      <div className="proposal-card" key={cat.id}>
                        <div className="proposal-header">
                          <div>
                            <div className="proposal-caterer">{cat.company_name}</div>
                            <div className="text-xs text-light">{cat.specialties?.join(", ")}</div>
                          </div>
                          <div style={{ textAlign: "right" }}>
                            <div className="proposal-price">€{(brief.budget_min * 1.1).toLocaleString()}</div>
                            <div className="text-xs text-light">€{Math.round(brief.budget_min * 1.1 / brief.guest_count)}/person</div>
                          </div>
                        </div>
                        <div className="proposal-body">{cat.description_en}</div>
                        <div className="flex gap-1 mt-1">
                          <button className="btn btn-sm btn-secondary">View Full Proposal</button>
                          <button className="btn btn-sm btn-ghost">Message</button>
                          <button className="btn btn-sm btn-primary">Award Contract</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {view === "caterers" && (
          <>
            <div className="dash-header">
              <h2>{t.caterers}</h2>
              <p>Browse verified catering companies on Caterbase</p>
            </div>
            <div className="card-grid cols-2">
              {MOCK_CATERERS.map(cat => (
                <div className="card" key={cat.id}>
                  <div className="flex justify-between items-center mb-1">
                    <div style={{ fontFamily: "Playfair Display, serif", fontWeight: 600, color: "var(--navy)" }}>{cat.company_name}</div>
                    <span className={`badge badge-${cat.verification_status}`}>{cat.verification_status}</span>
                  </div>
                  <div className="text-sm" style={{ color: "var(--text-mid)", marginBottom: "0.75rem" }}>{cat.description_en}</div>
                  <div className="flex gap-1" style={{ flexWrap: "wrap", marginBottom: "0.75rem" }}>
                    {cat.specialties?.map(s => (
                      <span key={s} style={{ background: "var(--sand-dark)", padding: "0.2rem 0.6rem", borderRadius: "100px", fontSize: "0.7rem", color: "var(--text-mid)", fontWeight: 500 }}>{s}</span>
                    ))}
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-light">{cat.price_range} · {cat.avg_rating ? `⭐ ${cat.avg_rating}` : "New"}</span>
                    <button className="btn btn-sm btn-ghost">View Profile</button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {view === "settings" && (
          <>
            <div className="dash-header"><h2>{t.settings}</h2></div>
            <div className="card" style={{ maxWidth: "560px" }}>
              <h3 style={{ fontFamily: "Playfair Display, serif", marginBottom: "1.25rem", color: "var(--navy)" }}>Organisation Details</h3>
              <div className="form-group">
                <label className="form-label">Organisation Name</label>
                <input className="form-input" defaultValue={user.organisation} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Contact Name</label>
                  <input className="form-input" defaultValue={user.full_name} />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input className="form-input" defaultValue={user.email} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Subscription Plan</label>
                <div className="alert alert-info">Professional plan — €299/month · 10 briefs per month · Renews 1 May 2026</div>
              </div>
              <button className="btn btn-primary btn-sm">{t.save}</button>
            </div>
          </>
        )}
      </div>

      {showBriefForm && <BriefForm onClose={() => setShowBriefForm(false)} onSubmit={handleNewBrief} lang={lang} />}
      {selectedBrief && <BriefDetail brief={selectedBrief} user={user} lang={lang} onClose={() => setSelectedBrief(null)} onPropose={() => {}} />}
    </div>
  );
}

function CatererDashboard({ user, lang, onLogout }) {
  const t = T[lang];
  const [view, setView] = useState("overview");
  const [selectedBrief, setSelectedBrief] = useState(null);
  const [proposalBrief, setProposalBrief] = useState(null);
  const [toast, setToast] = useState(null);
  const [openBriefs, setOpenBriefs] = useState(user.__demo ? MOCK_BRIEFS : []);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3500); };

  useEffect(() => {
    if (user.__demo) return;
    let cancelled = false;
    fetchOpenBriefs()
      .then(rows => { if (!cancelled) setOpenBriefs(rows); })
      .catch(err => { if (!cancelled) showToast(err.message); });
    return () => { cancelled = true; };
  }, [user.__demo]);

  const handleProposalSubmit = async (form) => {
    if (user.__demo || !proposalBrief) {
      setProposalBrief(null);
      showToast("Proposal submitted successfully!");
      return;
    }
    try {
      await insertProposal(form, proposalBrief.id);
      setProposalBrief(null);
      showToast("Proposal submitted successfully!");
    } catch (err) {
      showToast(err.message || "Failed to submit proposal");
    }
  };

  return (
    <div className="dashboard">
      <div className="sidebar">
        <div style={{ padding: "1rem 1.25rem 0.5rem", borderBottom: "1px solid var(--border)", marginBottom: "1rem" }}>
          <div style={{ fontWeight: 700, fontSize: "0.875rem", color: "var(--navy)" }}>{user.organisation}</div>
          <div style={{ fontSize: "0.75rem", color: "var(--text-light)" }}>{user.email}</div>
          <div style={{ marginTop: "0.5rem" }}>
            <span className="badge badge-approved">Verified Caterer</span>
          </div>
        </div>
        {[
          { id: "overview", icon: "◎", label: "Overview" },
          { id: "briefs", icon: "📋", label: "Open Briefs", badge: openBriefs.length },
          { id: "myproposals", icon: "📤", label: "My Proposals", badge: 2 },
          { id: "profile", icon: "🏢", label: "Company Profile" },
          { id: "settings", icon: "⚙", label: t.settings },
        ].map(item => (
          <div key={item.id} className={`sidebar-item ${view === item.id ? "active" : ""}`} onClick={() => setView(item.id)}>
            <span className="sidebar-icon">{item.icon}</span>
            {item.label}
            {item.badge && <span className="sidebar-badge">{item.badge}</span>}
          </div>
        ))}
        <div className="sidebar-item" style={{ color: "var(--error)" }} onClick={onLogout}>
          <span className="sidebar-icon">↩</span> {t.logout}
        </div>
      </div>

      <div className="dash-content">
        {toast && <div className="alert alert-success">{toast}</div>}

        {view === "overview" && (
          <>
            <div className="dash-header">
              <h2>Welcome, {user.full_name?.split(" ")[0]}</h2>
              <p>Browse open briefs and manage your proposals</p>
            </div>
            <div className="metrics">
              {[
                { v: openBriefs.length, l: "Open Briefs Available" },
                { v: 2, l: "Active Proposals" },
                { v: 1, l: "Contracts Won" },
                { v: "€18.5k", l: "Revenue via Caterbase" },
              ].map(m => (
                <div className="metric-card" key={m.l}><div className="metric-value">{m.v}</div><div className="metric-label">{m.l}</div></div>
              ))}
            </div>
            <div className="flex justify-between items-center mb-2">
              <h3 style={{ fontFamily: "Playfair Display, serif", color: "var(--navy)" }}>Latest Open Briefs</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setView("briefs")}>View all →</button>
            </div>
            <div className="card-grid cols-2">
              {openBriefs.slice(0, 4).map(b => (
                <BriefCard key={b.id} brief={b} lang={lang} onClick={setSelectedBrief} />
              ))}
            </div>
          </>
        )}

        {view === "briefs" && (
          <>
            <div className="dash-header">
              <h2>Open Briefs</h2>
              <p>All active catering opportunities on Caterbase</p>
            </div>
            <div className="card-grid cols-2">
              {openBriefs.map(b => (
                <BriefCard key={b.id} brief={b} lang={lang} onClick={setSelectedBrief} />
              ))}
            </div>
            {openBriefs.length === 0 && (
              <div className="empty">
                <div className="empty-icon">📋</div>
                <h3>No open briefs right now</h3>
                <p>Check back soon — new briefs are posted regularly.</p>
              </div>
            )}
          </>
        )}

        {view === "myproposals" && (
          <>
            <div className="dash-header"><h2>My Proposals</h2><p>Track the status of your submitted proposals</p></div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Brief</th><th>Organisation</th><th>Submitted Price</th><th>Status</th><th>Submitted</th></tr></thead>
                <tbody>
                  {MOCK_BRIEFS.slice(0, 2).map((b, i) => (
                    <tr key={b.id}>
                      <td><div className="font-semibold text-sm">{b.title_en.substring(0, 40)}...</div></td>
                      <td><div className="text-sm text-light">{b.org}</div></td>
                      <td><div className="font-semibold">€{(b.budget_min * 1.1).toLocaleString()}</div></td>
                      <td><span className={`badge badge-${i === 0 ? "shortlisted" : "submitted"}`}>{i === 0 ? "shortlisted" : "submitted"}</span></td>
                      <td><div className="text-sm text-light">3 days ago</div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {view === "profile" && (
          <>
            <div className="dash-header"><h2>Company Profile</h2><p>This is how buyers see your company on Caterbase</p></div>
            <div className="card" style={{ maxWidth: "640px" }}>
              <div className="form-group">
                <label className="form-label">Company Name</label>
                <input className="form-input" defaultValue={user.organisation} />
              </div>
              <div className="form-group">
                <label className="form-label">Description (English)</label>
                <textarea className="form-textarea" defaultValue={MOCK_CATERERS[0].description_en} />
              </div>
              <div className="form-group">
                <label className="form-label">Specialties</label>
                <div className="checkbox-group">
                  {["corporate","gala","cocktail","conference","buffet","sustainable","seated","outdoor"].map(s => (
                    <div className="checkbox-pill" key={s}>
                      <input type="checkbox" id={`sp-${s}`} defaultChecked={["corporate","gala","cocktail"].includes(s)} />
                      <label htmlFor={`sp-${s}`}>{s}</label>
                    </div>
                  ))}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Min. Guests</label>
                  <input className="form-input" type="number" defaultValue="20" />
                </div>
                <div className="form-group">
                  <label className="form-label">Max. Guests</label>
                  <input className="form-input" type="number" defaultValue="500" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Price Range</label>
                <select className="form-select">
                  {["budget","mid","premium","luxury"].map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <button className="btn btn-primary btn-sm">{t.save} Profile</button>
            </div>
          </>
        )}
      </div>

      {selectedBrief && (
        <BriefDetail brief={selectedBrief} user={user} lang={lang}
          onClose={() => setSelectedBrief(null)}
          onPropose={(b) => { setSelectedBrief(null); setProposalBrief(b); }}
        />
      )}
      {proposalBrief && (
        <ProposalForm brief={proposalBrief} lang={lang}
          onClose={() => setProposalBrief(null)}
          onSubmit={handleProposalSubmit}
        />
      )}
    </div>
  );
}

function AdminDashboard({ user, lang, onLogout }) {
  const [view, setView] = useState("overview");
  const [caterers, setCaterers] = useState(user.__demo ? MOCK_CATERERS : []);
  const [metrics, setMetrics] = useState(null);
  const [toast, setToast] = useState(null);
  const [rejecting, setRejecting] = useState(null);

  const showToast = (msg, type = "success") => { setToast({ msg, type }); setTimeout(() => setToast(null), 3500); };

  useEffect(() => {
    if (user.__demo) return;
    let cancelled = false;
    Promise.all([fetchAllCaterers(), fetchAdminMetrics()])
      .then(([cs, m]) => { if (!cancelled) { setCaterers(cs); setMetrics(m); } })
      .catch(err => { if (!cancelled) showToast(err.message, "error"); });
    return () => { cancelled = true; };
  }, [user.__demo]);

  const doApprove = async (id) => {
    if (user.__demo) {
      setCaterers(cs => cs.map(c => c.id === id ? { ...c, verification_status: "approved" } : c));
      showToast("Caterer approved");
      return;
    }
    try {
      const updated = await approveCaterer(id);
      setCaterers(cs => cs.map(c => c.id === id ? updated : c));
      showToast(`${updated.company_name} approved`);
    } catch (err) { showToast(err.message, "error"); }
  };

  const doReject = async (id, reason) => {
    if (user.__demo) {
      setCaterers(cs => cs.map(c => c.id === id ? { ...c, verification_status: "rejected", rejection_reason: reason } : c));
      showToast("Caterer rejected");
      return;
    }
    try {
      const updated = await rejectCaterer(id, reason);
      setCaterers(cs => cs.map(c => c.id === id ? updated : c));
      showToast(`${updated.company_name} rejected`);
    } catch (err) { showToast(err.message, "error"); }
  };

  const doSuspend = async (id) => {
    const reason = prompt("Suspension reason?");
    if (!reason) return;
    if (user.__demo) {
      setCaterers(cs => cs.map(c => c.id === id ? { ...c, verification_status: "suspended" } : c));
      showToast("Caterer suspended");
      return;
    }
    try {
      const updated = await suspendCaterer(id, reason);
      setCaterers(cs => cs.map(c => c.id === id ? updated : c));
      showToast(`${updated.company_name} suspended`);
    } catch (err) { showToast(err.message, "error"); }
  };

  const pendingCount = caterers.filter(c => c.verification_status === "pending").length;

  return (
    <div className="dashboard">
      <div className="sidebar">
        <div style={{ padding: "1rem 1.25rem 0.5rem", borderBottom: "1px solid var(--border)", marginBottom: "1rem" }}>
          <div style={{ fontWeight: 700, fontSize: "0.875rem", color: "var(--navy)" }}>Caterbase Admin</div>
          <div style={{ fontSize: "0.75rem", color: "var(--text-light)" }}>{user.email}</div>
          <div style={{ marginTop: "0.5rem" }}><span className="badge" style={{ background: "var(--copper)", color: "white" }}>Admin</span></div>
        </div>
        {["overview","caterers","buyers","briefs","subscriptions"].map(v => (
          <div key={v} className={`sidebar-item ${view === v ? "active" : ""}`} onClick={() => setView(v)}>
            <span className="sidebar-icon">{v === "overview" ? "◎" : v === "caterers" ? "🍽" : v === "buyers" ? "🏛" : v === "briefs" ? "📋" : "💳"}</span>
            {v.charAt(0).toUpperCase() + v.slice(1)}
            {v === "caterers" && pendingCount > 0 && <span className="sidebar-badge">{pendingCount}</span>}
          </div>
        ))}
        <div className="sidebar-item" style={{ color: "var(--error)" }} onClick={onLogout}><span className="sidebar-icon">↩</span> Log out</div>
      </div>
      <div className="dash-content">
        {toast && <div className={`alert alert-${toast.type}`}>{toast.msg}</div>}
        {view === "overview" && (
          <>
            <div className="dash-header"><h2>Platform Overview</h2><p>Real-time Caterbase metrics</p></div>
            <div className="metrics">
              {(user.__demo
                ? [{ v: "127", l: "Active Briefs" }, { v: "84", l: "Verified Caterers" }, { v: "1", l: "Pending Approval" }, { v: "€4.2M", l: "Total Contract Value" }, { v: "38", l: "Active Subscriptions" }, { v: "€9.8k", l: "MRR" }]
                : [
                    { v: metrics?.open_briefs ?? "—", l: "Active Briefs" },
                    { v: metrics?.approved_caterers ?? "—", l: "Verified Caterers" },
                    { v: metrics?.pending_caterers ?? "—", l: "Pending Approval" },
                    { v: metrics?.active_buyers ?? "—", l: "Active Subscriptions" },
                  ]
              ).map(m => (
                <div className="metric-card" key={m.l}><div className="metric-value">{m.v}</div><div className="metric-label">{m.l}</div></div>
              ))}
            </div>
            <h3 style={{ fontFamily: "Playfair Display, serif", color: "var(--navy)", marginBottom: "1rem" }}>Recent Activity</h3>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Event</th><th>Entity</th><th>Time</th></tr></thead>
                <tbody>
                  {[
                    { e: "New brief published", n: "NATO Headquarters", t: "12 min ago" },
                    { e: "Caterer approved", n: "Le Grand Buffet", t: "1 hour ago" },
                    { e: "Contract awarded", n: "European Commission", t: "3 hours ago" },
                    { e: "New subscription", n: "City of Brussels", t: "Yesterday" },
                    { e: "Proposal submitted", n: "KTCHN Brussels", t: "Yesterday" },
                  ].map((r, i) => (
                    <tr key={i}>
                      <td className="text-sm">{r.e}</td>
                      <td className="text-sm font-semibold">{r.n}</td>
                      <td className="text-xs text-light">{r.t}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        {view === "caterers" && (
          <>
            <div className="dash-header"><h2>Caterer Management</h2><p>Verify and manage catering companies</p></div>
            {pendingCount > 0 && (
              <div className="alert alert-info" style={{ marginBottom: "1rem" }}>
                <strong>{pendingCount}</strong> {pendingCount === 1 ? "caterer needs" : "caterers need"} review.
              </div>
            )}
            <div className="table-wrap">
              <table>
                <thead><tr><th>Company</th><th>VAT</th><th>Specialties</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>
                  {caterers.length === 0 && (
                    <tr><td colSpan="5" style={{ textAlign: "center", padding: "2rem", color: "var(--text-light)" }}>No caterers yet.</td></tr>
                  )}
                  {caterers.map(c => (
                    <tr key={c.id}>
                      <td className="font-semibold text-sm">{c.company_name}</td>
                      <td className="text-xs text-light">
                        {c.vat_number ? (
                          <a href={`https://kbopub.economie.fgov.be/kbopub/zoeknummerform.html?nummer=${c.vat_number.replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer" style={{ color: "var(--copper)" }}>
                            {c.vat_number} ↗
                          </a>
                        ) : "—"}
                      </td>
                      <td><div className="text-xs text-light">{c.specialties?.join(", ") || "—"}</div></td>
                      <td>
                        <span className={`badge badge-${c.verification_status}`}>{c.verification_status}</span>
                        {c.verification_status === "rejected" && c.rejection_reason && (
                          <div className="text-xs text-light" style={{ marginTop: "0.25rem" }}>{c.rejection_reason}</div>
                        )}
                      </td>
                      <td>
                        <div className="flex gap-1">
                          {c.verification_status === "pending" && <>
                            <button className="btn btn-sm btn-primary" onClick={() => doApprove(c.id)}>Approve</button>
                            <button className="btn btn-sm btn-ghost" style={{ color: "var(--error)" }} onClick={() => setRejecting(c)}>Reject</button>
                          </>}
                          {c.verification_status === "approved" && <button className="btn btn-sm btn-ghost" onClick={() => doSuspend(c.id)}>Suspend</button>}
                          {c.verification_status === "suspended" && <button className="btn btn-sm btn-primary" onClick={() => doApprove(c.id)}>Reinstate</button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {rejecting && (
              <Modal title={`Reject ${rejecting.company_name}`} onClose={() => setRejecting(null)}
                footer={<>
                  <button className="btn btn-ghost btn-sm" onClick={() => setRejecting(null)}>Cancel</button>
                  <button className="btn btn-primary btn-sm" onClick={() => {
                    const reason = document.getElementById("reject-reason")?.value;
                    if (!reason) { showToast("Reason required", "error"); return; }
                    doReject(rejecting.id, reason);
                    setRejecting(null);
                  }}>Reject caterer</button>
                </>}
              >
                <p className="text-sm text-light">The caterer will be notified with the reason. They can re-apply after fixing the issue.</p>
                <div className="form-group">
                  <label className="form-label required">Rejection reason</label>
                  <textarea id="reject-reason" className="form-textarea" rows="3" placeholder="e.g. VAT number not found in BCE/KBO register"></textarea>
                </div>
              </Modal>
            )}
          </>
        )}
        {view === "briefs" && (
          <>
            <div className="dash-header"><h2>All Briefs</h2></div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Reference</th><th>Title</th><th>Organisation</th><th>Deadline</th><th>Proposals</th><th>Status</th></tr></thead>
                <tbody>
                  {MOCK_BRIEFS.map(b => (
                    <tr key={b.id}>
                      <td className="text-xs text-light">{b.reference_number}</td>
                      <td className="text-sm font-semibold">{b.title_en.substring(0, 35)}...</td>
                      <td className="text-xs text-light">{b.org}</td>
                      <td className="text-sm">{b.deadline}</td>
                      <td className="text-sm">{b.proposal_count}</td>
                      <td><span className={`badge badge-${b.status}`}>{b.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        {view === "subscriptions" && (
          <>
            <div className="dash-header"><h2>Subscriptions</h2><p>Active buyer subscriptions</p></div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Organisation</th><th>Plan</th><th>MRR</th><th>Briefs Posted</th><th>Renewal</th><th>Status</th></tr></thead>
                <tbody>
                  {[
                    { org: "European Commission", plan: "Enterprise", mrr: "€599", briefs: 18, renewal: "2027-01-01" },
                    { org: "NATO HQ", plan: "Professional", mrr: "€299", briefs: 7, renewal: "2026-07-15" },
                    { org: "City of Brussels", plan: "Professional", mrr: "€299", briefs: 5, renewal: "2026-05-30" },
                    { org: "Belgian Federal Gov.", plan: "Starter", mrr: "€149", briefs: 2, renewal: "2026-04-22" },
                  ].map((s, i) => (
                    <tr key={i}>
                      <td className="font-semibold text-sm">{s.org}</td>
                      <td><span className="badge" style={{ background: "var(--sand-dark)", color: "var(--text-mid)" }}>{s.plan}</span></td>
                      <td className="font-semibold text-sm">{s.mrr}</td>
                      <td className="text-sm">{s.briefs}</td>
                      <td className="text-xs text-light">{s.renewal}</td>
                      <td><span className="badge badge-approved">active</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================
// APP ROOT
// ============================================================
export default function CaterbaseApp() {
  const [lang, setLang] = useState("en");
  const [user, setUser] = useState(null);
  const [authModal, setAuthModal] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [authBusy, setAuthBusy] = useState(false);
  const [page, setPage] = useState("landing");

  const t = T[lang];

  // Session restore + auth state change listener
  useEffect(() => {
    if (!hasSupabase) return;
    let cancelled = false;
    getCurrentUser().then(u => {
      if (cancelled) return;
      if (u && u.role) { setUser(u); setPage("dashboard"); }
    }).catch(() => {});
    const sub = onAuthChange(u => {
      if (cancelled) return;
      if (u && u.role) {
        setUser(prev => prev?.__demo ? prev : u);
        setPage(p => (u && u.role) ? "dashboard" : p);
      } else if (!u) {
        setUser(prev => prev?.__demo ? prev : null);
      }
    });
    return () => { cancelled = true; sub?.unsubscribe?.(); };
  }, []);

  const handleAuth = async (data) => {
    // Backwards-compatible: if data has `__demo`, treat as mock login (demoLogin path)
    if (data.__demo || !hasSupabase) {
      const mockUser = {
        ...data,
        __demo: true,
        id: data.id || "u1",
        role: data.role || "buyer",
        full_name: data.full_name || "Alex Dupont",
        organisation: data.organisation || (data.role === "caterer" ? "Artfood Traiteur" : "European Commission"),
        email: data.email || "a.dupont@ec.europa.eu",
      };
      setUser(mockUser);
      setAuthModal(null);
      setPage("dashboard");
      return;
    }
    setAuthError(null);
    setAuthBusy(true);
    try {
      const u = data.mode === "register"
        ? await authSignUp(data)
        : await authSignIn(data);
      if (!u || !u.role) {
        // Email confirmation likely required, or profile creation pending
        setAuthError("Check your email to confirm, then sign in.");
        return;
      }
      setUser(u);
      setAuthModal(null);
      setPage("dashboard");
    } catch (err) {
      setAuthError(err.message || String(err));
    } finally {
      setAuthBusy(false);
    }
  };

  const handleLogout = async () => {
    if (!user?.__demo) await authSignOut().catch(() => {});
    setUser(null);
    setPage("landing");
  };

  const demoLogin = (role) => {
    handleAuth({
      __demo: true,
      role,
      full_name: role === "admin" ? "Neil Ingram" : role === "buyer" ? "Sophie Laurent" : "Marc Dubois",
      organisation: role === "admin" ? "Caterbase" : role === "buyer" ? "European Commission" : "Artfood Traiteur",
      email: role === "admin" ? "neil@caterbase.be" : role === "buyer" ? "s.laurent@ec.europa.eu" : "m.dubois@artfood.be",
    });
  };

  return (
    <div className="app">
      <style>{STYLES}</style>

      {/* NAV */}
      <nav className="nav">
        <div className="nav-logo" onClick={() => { setPage("landing"); setUser(null); }}>
          Cater<span>base</span>
        </div>
        <div className="nav-links">
          <div className="lang-switcher">
            {["en","fr","nl"].map(l => (
              <button key={l} className={`lang-btn ${lang === l ? "active" : ""}`} onClick={() => setLang(l)}>{l}</button>
            ))}
          </div>
          {!user ? (
            <>
              {/* Demo buttons */}
              <button className="nav-btn" style={{ fontSize: "0.7rem", opacity: 0.6 }} onClick={() => demoLogin("buyer")}>Demo: Buyer</button>
              <button className="nav-btn" style={{ fontSize: "0.7rem", opacity: 0.6 }} onClick={() => demoLogin("caterer")}>Demo: Caterer</button>
              <button className="nav-btn" style={{ fontSize: "0.7rem", opacity: 0.6 }} onClick={() => demoLogin("admin")}>Demo: Admin</button>
              <button className="nav-btn" onClick={() => setAuthModal("login")}>{t.login}</button>
              <button className="nav-pill" onClick={() => setAuthModal("register")}>{t.postBrief}</button>
            </>
          ) : (
            <>
              <div className="nav-user">
                <span style={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.6)" }}>{user.organisation}</span>
                <div className="nav-avatar" onClick={handleLogout} title="Click to log out">
                  {user.full_name?.[0] || "U"}
                </div>
              </div>
            </>
          )}
        </div>
      </nav>

      {/* MAIN */}
      <main className="main">
        {page === "landing" && (
          <LandingPage lang={lang} onNav={setPage} onAuth={(m) => setAuthModal(m)} />
        )}
        {page === "dashboard" && user?.role === "buyer" && (
          <BuyerDashboard user={user} lang={lang} onLogout={handleLogout} />
        )}
        {page === "dashboard" && user?.role === "caterer" && (
          <CatererDashboard user={user} lang={lang} onLogout={handleLogout} />
        )}
        {page === "dashboard" && user?.role === "admin" && (
          <AdminDashboard user={user} lang={lang} onLogout={handleLogout} />
        )}
      </main>

      {/* FOOTER */}
      {page === "landing" && (
        <footer className="footer">
          <div>© 2026 Caterbase · <a href="#">Terms</a> · <a href="#">Privacy</a> · <a href="#">Contact</a></div>
          <div style={{ marginTop: "0.5rem" }}>Brussels · Belgium · EU Procurement Marketplace</div>
        </footer>
      )}

      {/* AUTH MODAL */}
      {authModal && (
        <AuthModal mode={authModal} lang={lang} onClose={() => { setAuthModal(null); setAuthError(null); }} onAuth={handleAuth} error={authError} busy={authBusy} />
      )}
    </div>
  );
}
